import { AsyncPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { FormsModule } from '@angular/forms';
import {
  GoogleMap,
  GoogleMapsModule,
  MapAdvancedMarker,
  MapInfoWindow,
} from '@angular/google-maps';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterLink } from '@angular/router';
import { select, Store } from '@ngrx/store';
import moment from 'moment';
import { lastValueFrom, map, Observable, Subject, switchMap, take, takeUntil } from 'rxjs';
import { Group, GroupType } from '../../../models/group.model';
import { MembershipStatus } from '../../../models/membership.status';
import { UserModel } from '../../../models/user.model';
import { ManagementAssignment } from '../../../models/management-assignment.model';
import { getRoleDisplayName } from '../../../models/hierarchy-node.model';
import { OrganizationRequest } from '../../../models/organization-request.model';
import { OrganizationRequestService } from '../../../services/organization-request.service';
import { InitialsPipe } from '../../../pipes/initials.pipe';
import { MomentPipe } from '../../../pipes/moment.pipe';
import { SearchService } from '../../../services/search.service';
import * as memberActions from '../../../state/actions/member.actions';
import * as ManagementActions from '../../../state/actions/management.actions';
import { AppState } from '../../../state/appstate';
import * as groupSelector from '../../../state/selectors/group.selector';
import * as ManagementSelectors from '../../../state/selectors/management.selectors';
import { BtnFabComponent } from '../../components/buttons/btn-fab/btn-fab.component';
import { BtnMemberStatusComponent } from '../../components/buttons/btn-member-status/btn-member-status.component';
import { BtnRouteComponent } from '../../components/buttons/btn-route/btn-route.component';
import { BtnWhatsappComponent } from '../../components/buttons/btn-whatsapp/btn-whatsapp.component';
import { AlertType } from '../../components/dialogs/confirm.dialog.model';
import { DialogService } from '../../components/dialogs/dialog.service';
import { DlgSelectGroupComponent } from '../../components/dialogs/dlg-select-group/dlg-select-group.component';

import { ViewLocationComponent } from '../../components/maps/view-location/view-location.component';
import { environment } from '../../../../environments/environment';

function getDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371; // Radius of the earth in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

@Component({
  selector: 'app-list-groups',
  standalone: true,
  templateUrl: './list-groups.component.html',
  imports: [
    AsyncPipe,
    BtnRouteComponent,
    BtnMemberStatusComponent,
    BtnFabComponent,
    MomentPipe,
    InitialsPipe,
    GoogleMapsModule,
    FormsModule,
    BtnWhatsappComponent,
    RouterLink,
    BtnFabComponent,
    ViewLocationComponent,
  ],
})
export class ListGroupsComponent implements OnInit, OnDestroy {
  router = inject(Router);
  http = inject(HttpClient);
  searchService = inject(SearchService);
  afs = inject(AngularFirestore);
  cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private dialogService = inject(DialogService);

  @ViewChild(GoogleMap) googleMap!: GoogleMap;
  @ViewChild(MapInfoWindow) infoWindow!: MapInfoWindow;
  @ViewChildren(MapAdvancedMarker) mapMarkers!: QueryList<MapAdvancedMarker>;

  user: UserModel | undefined | null;

  groups$: Observable<Group[]> = new Observable();
  loadingGroups$: Observable<boolean> = new Observable();
  myManagementAssignments$: Observable<ManagementAssignment[]>;
  // One entry per organization, however many roles the user holds in it
  myManagementNetworks$: Observable<ManagementNetworkCard[]>;
  // Requests to create an organization that are pending, or were rejected in the last 30 days
  myOpenOrgRequests$: Observable<OrganizationRequest[]>;
  private orgRequestService = inject(OrganizationRequestService);

  headers: string[] = ['Group Name', 'City', 'Membership Status', 'Joined'];

  membershipStatus = MembershipStatus;

  logout$ = new Subject<void>();

  // Communities Around Me search state
  nearbyGroups: Group[] = [];
  loadingNearby: boolean = false;
  nearbyError: string | null = null;
  locationSource: string = '';
  searchText: string = '';
  distance: string = '25km';
  availableDistances = ['5km', '10km', '25km', '50km', '100km'];

  // Info Window & Detail Modal State
  infoWindowGroup: Group | null = null;
  activeDetailItem: Group | null = null;
  activeDetailEvent: any | null = null;
  joinedGroups: Set<string> = new Set();

  // Nearby Events state
  nearbyEvents: any[] = [];
  loadingEvents: boolean = false;

  whatsAppMessage: string = `Hi! I am using AtChurch to connect and find communities, activities, and events around me. Check it out and join here: https://atchurch.app`;
  whatsAppInviteLink: string = `https://api.whatsapp.com/send?text=${encodeURIComponent(this.whatsAppMessage)}`;

  mapOptions: google.maps.MapOptions = {
    mapId: 'b45898a1f3fbad8919f6191f',
    mapTypeId: 'roadmap',
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: false,
    zoom: 11,
    fullscreenControl: false,
    streetViewControl: false,
  };
  mapCenter: google.maps.LatLngLiteral = { lat: 25.2697, lng: 55.3095 };
  userLocation: google.maps.LatLngLiteral | null = null;
  userLocationMarkerContent: HTMLElement | null = null;
  markers: any[] = [];
  selectedGroup: Group | null = null;

  constructor(private store: Store<AppState>) {
    this.myManagementAssignments$ = this.store.select(
      ManagementSelectors.selectMyManagementAssignments
    );
    this.myManagementNetworks$ = this.myManagementAssignments$.pipe(
      map((assignments) => groupAssignmentsByOrganization(assignments || []))
    );
    const monthAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    this.myOpenOrgRequests$ = this.store
      .select((state) => state.user.user?.uid || '')
      .pipe(
        switchMap((uid) => this.orgRequestService.getMyRequests(uid)),
        map((list) =>
          list.filter(
            (r) =>
              r.status === 'pending' ||
              (r.status === 'rejected' && (r.reviewedAt?.toMillis?.() ?? 0) > monthAgo)
          )
        )
      );
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.logout$))
      .subscribe((user) => {
        this.user = user;
        if (user?.uid) {
          this.store.dispatch(
            ManagementActions.loadMyAssignments({ userId: user.uid })
          );
        }
        // If address changes or loads, and we haven't resolved location yet, we can resolve
        if (user && this.nearbyGroups.length === 0 && !this.loadingNearby) {
          this.resolveLocation();
        }
      });
    this.groups$ = this.store.pipe(select(groupSelector.getAllGroups));
    this.loadingGroups$ = this.store.select((state) => state.groups.loading);
  }

  ngOnDestroy(): void {
    this.logout$.next();
    this.logout$.complete();
  }

  ngOnInit(): void {
    const redirectUrl = localStorage.getItem('redirectAfterLogin');
    if (redirectUrl) {
      localStorage.removeItem('redirectAfterLogin');
      this.router.navigateByUrl(redirectUrl);
    }
    // Initialize user location marker content
    if (typeof document !== 'undefined') {
      const div = document.createElement('div');
      div.className = 'relative flex items-center justify-center h-4 w-4';

      const pulse = document.createElement('div');
      pulse.className =
        'absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75 animate-ping';

      const dot = document.createElement('div');
      dot.className =
        'relative inline-flex rounded-full h-3 w-3 bg-blue-600 border border-white';

      div.appendChild(pulse);
      div.appendChild(dot);
      this.userLocationMarkerContent = div;
    }
    // Start resolving location on load
    this.resolveLocation();
  }

  getUserMarkerOptions(): google.maps.MarkerOptions {
    if (
      typeof google !== 'undefined' &&
      google.maps &&
      google.maps.SymbolPath
    ) {
      return {
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 7.5,
          fillColor: '#2563EB',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 3,
        },
        title: 'Your Current Location',
        zIndex: 9999,
      };
    }
    return { title: 'Your Current Location' };
  }

  getChurchMarkerIcon(): google.maps.Icon | string {
    if (typeof google !== 'undefined' && google.maps && google.maps.Size) {
      return {
        url: 'assets/markers/marker_church.png',
        scaledSize: new google.maps.Size(28, 34),
      };
    }
    return 'assets/markers/marker_church.png';
  }

  async resolveLocation() {
    this.loadingNearby = true;
    this.nearbyError = null;
    this.selectedGroup = null;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          this.userLocation = { lat, lng };
          this.searchNearby(lat, lng, 'Browser Geolocation');
        },
        (error) => {
          console.warn(
            'Geolocation failed or denied, trying IP-based location:',
            error,
          );
          this.resolveIpLocation();
        },
        { timeout: 10000, enableHighAccuracy: false, maximumAge: 60000 },
      );
    } else {
      console.warn(
        'Browser geolocation not supported, trying IP-based location',
      );
      this.resolveIpLocation();
    }
  }

  async resolveIpLocation() {
    try {
      const res = await lastValueFrom(
        this.http.get<any>('https://ipapi.co/json/').pipe(take(1)),
      );
      if (res && res.latitude && res.longitude) {
        this.userLocation = { lat: res.latitude, lng: res.longitude };
        this.searchNearby(res.latitude, res.longitude, 'IP Geolocation');
      } else {
        throw new Error('Invalid IP location format');
      }
    } catch (err) {
      console.warn(
        'IP location resolution failed, trying Profile location:',
        err,
      );
      this.resolveProfileLocation();
    }
  }

  async resolveProfileLocation() {
    const city = this.user?.address?.city;
    const country = this.user?.address?.country;
    if (city || country) {
      const addressStr = [city, country].filter(Boolean).join(', ');
      try {
        const coords = await this.geocodeAddress(addressStr);
        this.userLocation = coords;
        this.searchNearby(
          coords.lat,
          coords.lng,
          `Profile Location (${addressStr})`,
        );
      } catch (err) {
        console.warn('Profile geocoding failed, using default location:', err);
        this.useDefaultLocation();
      }
    } else {
      console.warn('No location info in user profile, using default location.');
      this.useDefaultLocation();
    }
  }

  useDefaultLocation() {
    // Default to Dubai coordinates
    this.searchNearby(25.2697, 55.3095, 'Default Location (Dubai)');
  }

  geocodeAddress(addressStr: string): Promise<google.maps.LatLngLiteral> {
    return new Promise((resolve, reject) => {
      if (typeof google === 'undefined' || !google.maps) {
        reject(new Error('Google Maps API not loaded'));
        return;
      }
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: addressStr }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const lat = results[0].geometry.location.lat();
          const lng = results[0].geometry.location.lng();
          resolve({ lat, lng });
        } else {
          reject(new Error(`Geocoding failed with status: ${status}`));
        }
      });
    });
  }

  async searchNearby(lat: number, lon: number, source: string) {
    this.loadingNearby = true;
    this.loadingEvents = true;
    this.nearbyError = null;
    this.mapCenter = { lat, lng: lon };
    this.locationSource = source;

    this.mapOptions = {
      ...this.mapOptions,
      center: this.mapCenter,
    };
    if (this.googleMap?.googleMap) {
      this.googleMap.googleMap.panTo(this.mapCenter);
    }

    try {
      let groups: Group[] = [];
      let events: any[] = [];

      try {
        const [fetchedGroups, fetchedEvents] = await Promise.all([
          this.searchService.getGroupsByLocation(lat, lon, this.distance, 20),
          this.searchService.getEventsByLocation(lat, lon, this.distance, 15),
        ]);
        groups = fetchedGroups || [];
        events = (fetchedEvents || []).filter((e: any) => e.isPrivate !== true);
      } catch (apiErr) {
        console.warn(
          'AWS location search API failed on home page, trying fallback:',
          apiErr,
        );
      }

      // Fallback: If AWS location API returned 0 groups, fetch directly from Firestore groups & filter by distance
      if (groups.length === 0) {
        try {
          const groupsSnapshot = await lastValueFrom(
            this.afs.collection<Group>('groups').get().pipe(take(1)),
          );
          const limitDistanceKm = parseFloat(this.distance.replace('km', ''));
          const rawGroups = groupsSnapshot.docs.map((doc) => {
            const data = doc.data() as Group;
            return {
              ...data,
              id: doc.id,
            };
          });

          groups = rawGroups.filter((g) => {
            const loc = g.location as any;
            const gLat = loc?.position?.latitude;
            const gLng = loc?.position?.longitude;
            if (gLat && gLng) {
              const dist = getDistance(lat, lon, gLat, gLng);
              return dist <= limitDistanceKm;
            }
            return false;
          });
        } catch (fsErr) {
          console.error(
            'Failed to load fallback Firestore groups on home page:',
            fsErr,
          );
        }
      }

      // Fallback: If AWS location API returned 0 events, fetch directly from Firestore events
      if (events.length === 0) {
        try {
          const eventsSnapshot = await lastValueFrom(
            this.afs.collection<any>('events').get().pipe(take(1)),
          );
          const limitDistanceKm = parseFloat(this.distance.replace('km', ''));
          const rawEvents = eventsSnapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              ...data,
              id: doc.id,
            };
          });

          const now = moment();
          const maxStartDate = moment().add(5, 'days');

          events = rawEvents
            .filter((e) => {
              if (e.isPrivate === true) return false;

              const loc = e.location as any;
              const eLat = loc?.position?.latitude;
              const eLng = loc?.position?.longitude;
              if (eLat && eLng) {
                const dist = getDistance(lat, lon, eLat, eLng);
                if (dist > limitDistanceKm) return false;
              }

              const start = e.startDateTime?.toDate
                ? moment(e.startDateTime.toDate())
                : e.startDateTime
                  ? moment(e.startDateTime)
                  : null;
              const end = e.endDateTime?.toDate
                ? moment(e.endDateTime.toDate())
                : e.endDateTime
                  ? moment(e.endDateTime)
                  : null;

              const isStartValid = start
                ? start.isSameOrBefore(maxStartDate)
                : true;
              const isEndValid = end
                ? end.isSameOrAfter(now)
                : start
                  ? start.isSameOrAfter(now)
                  : true;

              return isStartValid && isEndValid;
            })
            .slice(0, 15);
        } catch (evErr) {
          console.warn(
            'Failed to load fallback Firestore events on home page:',
            evErr,
          );
        }
      }

      this.nearbyGroups = groups;
      this.nearbyEvents = events;

      this.markers = this.nearbyGroups
        .filter(
          (g) =>
            g.location?.position?.latitude && g.location?.position?.longitude,
        )
        .map((g) => {
          const img =
            typeof document !== 'undefined'
              ? document.createElement('img')
              : null;
          if (img) {
            img.src = 'assets/markers/marker_church.png';
            img.style.width = '28px';
            img.style.height = '34px';
          }
          return {
            id: g.id,
            position: {
              lat: g.location!.position!.latitude,
              lng: g.location!.position!.longitude,
            },
            title: g.name,
            content: img,
            group: g,
          };
        });

      this.loadingNearby = false;
      this.loadingEvents = false;
      this.cdr.detectChanges();
    } catch (err) {
      console.error('Failed to load nearby communities or events:', err);
      // Fail silently per user request
      this.nearbyGroups = [];
      this.nearbyEvents = [];
      this.markers = [];
      this.loadingNearby = false;
      this.loadingEvents = false;
      this.cdr.detectChanges();
    }
  }

  onCreateEventClick() {
    this.groups$.pipe(take(1)).subscribe((groups) => {
      if (!groups || groups.length === 0) {
        this.dialogService
          .confirm({
            title: 'Not Member to Any Community',
            description:
              'You are not currently a member of any community. Would you like to create a new community?',
            action: 'Create Community',
            cancelText: 'Cancel',
            showType: AlertType.info,
          })
          .then((confirmed) => {
            if (confirmed) {
              this.router.navigate(['/groups/create']);
            }
          });
      } else {
        const dialogRef = this.dialog.open(DlgSelectGroupComponent, {
          data: { groups },
          width: '420px',
          maxWidth: '90vw',
          autoFocus: false,
          panelClass: 'custom-dialog-no-padding',
        });

        dialogRef.afterClosed().subscribe((selectedGroup: Group | null) => {
          if (selectedGroup) {
            this.router.navigate([
              '/groups',
              selectedGroup.id,
              'events',
              'create',
            ]);
          }
        });
      }
    });
  }

  onSearchLocation() {
    if (!this.searchText.trim()) {
      this.resolveLocation();
      return;
    }
    this.loadingNearby = true;
    this.geocodeAddress(this.searchText)
      .then((coords) => {
        this.searchNearby(
          coords.lat,
          coords.lng,
          `Search Query: "${this.searchText}"`,
        );
      })
      .catch((err) => {
        console.error('Manual search geocoding failed:', err);
        this.nearbyError = `Could not find location: "${this.searchText}". Please check spelling and try again.`;
        this.loadingNearby = false;
        this.cdr.detectChanges();
      });
  }

  onDistanceChange() {
    this.searchNearby(
      this.mapCenter.lat,
      this.mapCenter.lng,
      this.locationSource,
    );
  }

  selectGroup(group: Group, markerComponent?: MapAdvancedMarker) {
    this.selectedGroup = group;
    if (
      group.location?.position?.latitude &&
      group.location?.position?.longitude
    ) {
      this.mapCenter = {
        lat: group.location.position.latitude,
        lng: group.location.position.longitude,
      };
      this.mapOptions = {
        ...this.mapOptions,
        center: this.mapCenter,
        zoom: 13,
      };
      if (this.googleMap?.googleMap) {
        this.googleMap.googleMap.panTo(this.mapCenter);
      }
    }

    if (markerComponent) {
      this.infoWindowGroup = group;
      this.infoWindow?.open(markerComponent);
    } else {
      const matchedMarker = this.mapMarkers?.find((m) => {
        const pos = m.position;
        if (!pos) return false;

        let lat: number;
        let lng: number;
        if (typeof pos.lat === 'function') {
          lat = (pos as google.maps.LatLng).lat();
          lng = (pos as google.maps.LatLng).lng();
        } else {
          lat = (pos as google.maps.LatLngLiteral).lat;
          lng = (pos as google.maps.LatLngLiteral).lng;
        }

        const gLat = group.location?.position?.latitude;
        const gLng = group.location?.position?.longitude;
        return (
          gLat !== undefined &&
          gLng !== undefined &&
          Math.abs(lat - gLat) < 0.0001 &&
          Math.abs(lng - gLng) < 0.0001
        );
      });
      if (matchedMarker) {
        this.infoWindowGroup = group;
        this.infoWindow?.open(matchedMarker);
      }
    }
    this.cdr.detectChanges();
  }

  openDetailModal(group: Group) {
    this.activeDetailItem = group;
    this.cdr.detectChanges();
  }

  closeDetailModal() {
    this.activeDetailItem = null;
    this.cdr.detectChanges();
  }

  joinGroup(group: Group) {
    if (!this.user?.uid) {
      localStorage.setItem('redirectAfterLogin', this.router.url);
      this.router.navigate(['/auth/login']);
      return;
    }
    this.store.dispatch(
      memberActions.requestMembership({
        group: group,
        uid: this.user.uid,
      }),
    );
    this.joinedGroups.add(group.id);
    this.cdr.detectChanges();
  }

  getShareLink(group: Group): string {
    const msg = `Check out ${group.name} on AtChurch: https://${environment.domain}/links/groupinvite?groupId=${group.id}`;
    return `https://api.whatsapp.com/send?text=${encodeURIComponent(msg)}`;
  }

  openEventDetailModal(event: any) {
    this.activeDetailEvent = event;
    this.cdr.detectChanges();
  }

  closeEventDetailModal() {
    this.activeDetailEvent = null;
    this.cdr.detectChanges();
  }

  getEventShareLink(event: any): string {
    const groupName =
      this.getGroupForEvent(event)?.name ||
      event.groupName ||
      'Community Event';
    const msg = `Check out ${event.name} hosted by ${groupName} on AtChurch: https://atchurch.app`;
    return `https://api.whatsapp.com/send?text=${encodeURIComponent(msg)}`;
  }

  getGroupForEvent(event: any): Group | undefined {
    if (!event?.groupId) return undefined;
    return this.nearbyGroups?.find((g) => g.id === event.groupId);
  }

  onClick(item: Group) {
    this.router.navigate(
      item.groupType != GroupType.school
        ? ['../groups', item?.id]
        : ['../school', item?.id],
    );
  }
}

export interface ManagementNetworkCard {
  organizationId: string;
  organizationName: string;
  status: string;
  // The user's most senior role in the org (closest to the top of the chart)
  primary: ManagementAssignment;
  primaryTitle: string;
  otherRoleCount: number;
}

/** Groups a user's role assignments into one card per organization */
export function groupAssignmentsByOrganization(assignments: ManagementAssignment[]): ManagementNetworkCard[] {
  const byOrg = new Map<string, ManagementAssignment[]>();
  for (const a of assignments) {
    const list = byOrg.get(a.organizationId) || [];
    list.push(a);
    byOrg.set(a.organizationId, list);
  }
  return [...byOrg.values()].map((list) => {
    // Highest tier first; on a tie, a directly assigned role beats one synced from a church
    const sorted = [...list].sort(
      (a, b) =>
        (a.tierLevel ?? Number.MAX_SAFE_INTEGER) - (b.tierLevel ?? Number.MAX_SAFE_INTEGER) ||
        Number(a.source === 'church_affiliation') - Number(b.source === 'church_affiliation')
    );
    const primary = sorted[0];
    return {
      organizationId: primary.organizationId,
      organizationName: list.find((a) => a.organizationName)?.organizationName || '',
      status: list.some((a) => a.status === 'active') ? 'active' : primary.status,
      primary,
      primaryTitle: getRoleDisplayName(
        { name: primary.nodeName, levelName: '', isChurch: primary.source === 'church_affiliation' },
        { id: primary.roleId, name: primary.roleName }
      ),
      otherRoleCount: list.length - 1,
    };
  });
}
